// Copyright 2020 Sorama B.V.

import { PLATFORM } from "aurelia-pal";
import { GuidanceTopicType, IGuidanceTopic } from "catalogs/GuidanceCatalog";
import { WorkflowModuleType } from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { WorkflowModes } from "services/WorkflowModes/WorkflowModes";

export class FarFieldAnalysisGuidanceCatalog {
    static FarFieldAnalysisOverviewModule: IGuidanceTopic = {
        name: "FarFieldAnalysis_Overview",
        modulePath: PLATFORM.moduleName("guidance/FarFieldAnalysis/Overview.html"),
        title: "Far Field Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicType: GuidanceTopicType.TopicOverview,
        topicContent: "Far Field Analysis Help",
    };

    static FarFieldAnalysisOverviewStreamingModule: IGuidanceTopic = {
        name: "FarFieldAnalysis_OverviewStreaming",
        modulePath: PLATFORM.moduleName("guidance/FarFieldAnalysis/OverviewStreaming.html"),
        title: "Streaming Far Field",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicType: GuidanceTopicType.TopicOverview,
        topicContent: "Streaming Far Field Help",
    };
    static FarFieldAnalysisGS1Module: IGuidanceTopic = {
        name: "FarFieldAnalysis_Q1", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720044477/FF1+How+do+I+recognize+and+avoid+pressure+ghost+sources+Aliasing
        modulePath: PLATFORM.moduleName("guidance/FarFieldAnalysis/FF1PressureGhostSources.html"),
        title: "Far Field Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: FarFieldAnalysisGuidanceCatalog.FarFieldAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How do I recognize and avoid pressure ghost sources?",
        catchwords: [""],
    };

    static FarFieldAnalysisGS2Module: IGuidanceTopic = {
        name: "FarFieldAnalysis_Q2", // https://sorama.atlassian.net/wiki/spaces/UI/pages/719849005/FF2+How+do+I+differentiate+localize+Single+source+and+Multiple+sources
        modulePath: PLATFORM.moduleName("guidance/FarFieldAnalysis/FF2SingleSourceAndMultipleSource.html"),
        title: "Far Field Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: FarFieldAnalysisGuidanceCatalog.FarFieldAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How do I differentiate & localize Single source and Multiple sources?",
        catchwords: ["Streaming", "select frequency (band)", "far field", "near filed"],
        hidden: true,
    };
    static FarFieldAnalysisGS3Module: IGuidanceTopic = {
        name: "FarFieldAnalysis_Q3", // https://sorama.atlassian.net/wiki/spaces/UI/pages/719849063/FF3+How+to+localize+sound
        modulePath: PLATFORM.moduleName("guidance/FarFieldAnalysis/FF3HowToLocalizeSound.html"),
        title: "Far Field Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: FarFieldAnalysisGuidanceCatalog.FarFieldAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to localize sound?",
        catchwords: [""],
        hidden: true,
    };
    static FarFieldAnalysisGS4Module: IGuidanceTopic = {
        name: "FarFieldAnalysis_Q4", // https://sorama.atlassian.net/wiki/spaces/UI/pages/719849109/FF4+When+should+I+select+Time+Averaged+in+the+Far+Field+analysis+tool
        modulePath: PLATFORM.moduleName("guidance/FarFieldAnalysis/FF4TimeAveragedFarField.html"),
        title: "Far Field Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: FarFieldAnalysisGuidanceCatalog.FarFieldAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "When should I select ‘Time Averaged’ in the Far Field analysis tool?",
        catchwords: [""],
    };
    static FarFieldAnalysisGS5Module: IGuidanceTopic = {
        name: "FarFieldAnalysis_Q5", //https://sorama.atlassian.net/wiki/spaces/UI/pages/825327736/FF5+What+are+Far+Field+probes
        modulePath: PLATFORM.moduleName("guidance/FarFieldAnalysis/FF5WhatAreFarFieldProbes.html"),
        title: "Far Field Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicType: GuidanceTopicType.TopicOverview,
        topicParent: FarFieldAnalysisGuidanceCatalog.FarFieldAnalysisOverviewModule,
        topicContent: "Far Field Analysis Probes",
    };
    static FarFieldAnalysisGS6Module: IGuidanceTopic = {
        name: "FarFieldAnalysis_Q6", //https://sorama.atlassian.net/wiki/spaces/UI/pages/825491565/FF6+What+is+a+Far+Field+Analysis
        modulePath: PLATFORM.moduleName("guidance/FarFieldAnalysis/FF6WhatIsFarfield.html"),
        title: "Far Field Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicType: GuidanceTopicType.TopicOverview,
        topicParent: FarFieldAnalysisGuidanceCatalog.FarFieldAnalysisOverviewModule,
        topicContent: "What is Far Field Analysis?",
    };
    static FarFieldAnalysisGS7Module: IGuidanceTopic = {
        name: "FarFieldAnalysis_Q7", //https://sorama.atlassian.net/wiki/spaces/UI/pages/825327899/FF7+Quick+start+for+using+the+Far+Field+analysis
        modulePath: PLATFORM.moduleName("guidance/FarFieldAnalysis/FF7FarFieldQuickStart.html"),
        title: "Far Field Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicType: GuidanceTopicType.TopicOverview,
        topicParent: FarFieldAnalysisGuidanceCatalog.FarFieldAnalysisOverviewModule,
        topicContent: "Far Field Analysis Quick Start",
    };

    static FarFieldAnalysisGS8Module: IGuidanceTopic = {
        name: "FarFieldAnalysis_Q8", // https://sorama.atlassian.net/wiki/spaces/UI/pages/831160373/FF8+What+are+the+frequency+boundaries+for+Far+Field+analysis
        modulePath: PLATFORM.moduleName("guidance/FarFieldAnalysis/FF8WhatAretheFrequencyBoundariesforFarField.html"),
        title: "Far Field Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: FarFieldAnalysisGuidanceCatalog.FarFieldAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "What are the frequency boundaries for Far Field analysis?",
        catchwords: [""],
    };
}
